import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import tableEmptyStateAdd from '@/assets/images/table/tableEmptyStateInvoice.svg'
import {
    Button,
    Card, CompanyFilter,
    DatePicker,
    IconDownload, IconExpand01, IconMinimize01,
    Image,
    InvoiceSendPopup
} from '@/components'
import { IndexContainer } from '@/containers'
import { useAuth, useIndexContainer, useAnalytics } from '@/hooks'
import { Model, WarrantySummaryItem } from '@/models'
import {
    DateRangeType,
    IndexContainerChangeEvent, IndexContainerFilterValueType,
    TableRowType,
    WarrantySummaryItemIndexDataType,
    WarrantySummaryItemIndexFiltersType
} from '@/types'

const WARRANTY_SUMMARY_INDEX_FILTERS: WarrantySummaryItemIndexFiltersType = {
    warranty_summary_search: {
        label: 'Search',
        defaultValue: '',
        value: '',
        isStandalone: true
    },
    warranty_summary_date_range: {
        label: 'Date',
        defaultValue: [new Date().startOf('month'), new Date().endOf('month')],
        value: [new Date().startOf('month'), new Date().endOf('month')],
        isStandalone: true,
        convert: (value: IndexContainerFilterValueType) => value
            ? (value as [string, string]).map(Model.toDate) as DateRangeType
            : [new Date().startOf('month'), new Date().endOf('month')]
    },
    warranty_summary_company_id: {
        label: 'Company',
        defaultValue: '',
        value: '',
        isStandalone: true
    }
}

export const WarrantySummaryItemIndex = () => {
    const auth = useAuth()
    const { trackEvent } = useAnalytics()

    const [isInfoOpen, setIsInfoOpen] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const isExpanded = searchParams.get('warranty_summary_expanded') === 'true'
    const { filters, handleFiltersChange } = useIndexContainer(WARRANTY_SUMMARY_INDEX_FILTERS, 'Adjusted Warranty Summary')

    const [selectedRow, setSelectedRow] = useState<WarrantySummaryItem | null>(null)
    const [data, setData] = useState<WarrantySummaryItemIndexDataType>({
        meta: {
            total: 0,
            overall_count: 0
        },
        rows: [],
        selectedRows: []
    })

    const handleChange = (e: IndexContainerChangeEvent) => {
        setData(data => ({ ...data, [e.name]: e.value }))
    }

    const selected = useMemo(() => (data.selectedRows as TableRowType[]).map(item =>
        data.rows.find(row => row.id === item.id) as WarrantySummaryItem)
    , [data.rows, data.selectedRows])

    const handleDownloadClick = () => {
        WarrantySummaryItem.downloadMany(selected)
    }

    const rowsMemo = useMemo(() => data.rows.map(item => ({
        id: item.id,
        date: item.date,
        date_raw: new Date(`1 ${item.date}`),
        company: item.row_name,
        num_warranties: item.total_warranties.format(),
        num_warranties_raw: item.total_warranties,
        num_panels: item.panels_number.format(),
        num_panels_raw: item.panels_number,
        total: item.sales.money(),
        total_raw: item.sales,
        _path: `/warranty-summary/${item.id}`
    })).filter(item => !filters.warranty_summary_search.value || Object.values(item).some(item => {
        if (typeof item?.toString === 'function') {
            return item.toString().toLowerCase().includes(filters.warranty_summary_search?.value.toLowerCase())
        }
        return true
    })), [data.rows])

    const handleToggleInfoClick = () => {
        trackEvent('button_click', 'adjusted_warranty_summary', 'Toggle Info Button')
        setIsInfoOpen(isInfoOpen => !isInfoOpen)
    }

    const handleToggleClick = () => {
        trackEvent('button_click', 'adjusted_warranty_summary', 'Toggle Button')
        const next = (!isExpanded).toString()
        searchParams.set('warranty_summary_expanded', next)
        setSearchParams(searchParams)
    }

    return <>
        <Card>
            <div className="flex justify-between">
                <h2 className="text-2xl flex flex-wrap gap-2">
                    Adjusted Warranty Summary
                    <Button
                        design="btn-link"
                        className="text-primary-800"
                        onClick={() => handleToggleInfoClick()}
                    >
                        {isInfoOpen ? 'Show Less' : 'Learn More'}
                    </Button>
                </h2>

                <Button
                    square
                    design="btn-secondary-gray"
                    className="shrink-0"
                    onClick={handleToggleClick}
                    tooltip={isExpanded? 'Minimize' : 'Maximize'}
                >
                    {isExpanded
                        ? <IconMinimize01 className="animate-fade-in"/>
                        : <IconExpand01 className="animate-fade-in"/>}
                </Button>
            </div>
            {isInfoOpen && <div className="rounded-lg animate-fade-in p-4 bg-gray-100 text-sm text-gray-750 flex
             flex-col gap-2.5 mt-2">
                <p>
                    <span className="font-semibold">📌 Description:</span> This table offers a real-time view of your
                    currently active warranties and panels for each month. It reflects any post-invoice changes,
                    including cancellations, size updates, or late additions. Because warranties can be modified after
                    invoicing, this data may not match the original invoice records.
                </p>
                <p>
                    <span className="font-semibold">🛟 Need Help?</span> For reconciliation, compare this summary with
                    the Official Invoice Records above or contact <a
                        href="mailto:accounting@solarinsure.com"
                        className="underline italic"
                    >accounting@solarinsure.com</a> or your account manager for assistance.
                </p>
            </div>}
            {isExpanded && <IndexContainer
                id="invoices"
                dataName="Invoices"
                className="animate-fade-in mt-6"
                searchFilterKey="warranty_summary_search"
                api={(params, ...args) =>
                    WarrantySummaryItem.index({ activity_tracker: 'invoice.index', ...params }, ...args)}
                data={{
                    ...data,
                    meta: {
                        ...data.meta,
                        total: rowsMemo.length
                    }
                }}
                onDataChange={handleChange}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                tableOptions={{
                    dataName: 'Invoices',
                    selectable: true,
                    prefix: 'warranty_summary'
                }}
                columns={[
                    { field: 'date', title: 'Accounting Period' },
                    { field: 'company', title: 'Company', showIf: auth.user?.role.isAdminOrStaffOrAdvisor },
                    { field: 'num_warranties', title: '# of Warranties' },
                    { field: 'num_panels', title: '# of Panels' },
                    { field: 'total', title: 'Total' }
                ]}
                rows={rowsMemo}
                leftSide={<>
                    <DatePicker
                        id="warranty-summary-date-range-filter"
                        name="warranty_summary_date_range"
                        placeholder="Date range"
                        value={filters.warranty_summary_date_range.value}
                        onChange={handleFiltersChange}
                        singleMode={false}
                        maxDate={new Date()}
                        className="md:min-w-75"
                    />
                    {!auth.user?.role.isCertifiedProvider &&
                        <CompanyFilter
                            id="warranty-summary-company-id"
                            name="warranty_summary_company_id"
                            placeholder="Company"
                            onChange={handleFiltersChange}
                            value={filters.warranty_summary_company_id.value}
                            className="md:min-w-75"
                        />}
                </>}
                rightSide={<>
                    <Button
                        square
                        onClick={() => handleDownloadClick()}
                        disabled={!data.selectedRows?.length}
                        design="btn-secondary-gray"
                        size="btn-lg"
                        tooltip={data.selectedRows?.length
                            ? 'Download Invoice Data' : 'Select at least one row to download data.'}
                        tooltipDisabled={false}
                    >
                        <IconDownload/>
                    </Button>
                </>}
                placeholder={data.meta?.overall_count === 0 && <>
                    <Image
                        className="w-30"
                        src={tableEmptyStateAdd}
                    />
                    <div className="mt-4 flex flex-col items-center">
                        <h4 className="text-center font-light">Nothing to Invoice, Nothing to Worry!</h4>
                        <p className="text-center text-gray-500 font-light text-sm mt-1">
                            There are currently no invoices to display. You{'\''}re all set! We{'\''}ll start populating
                            this table once you{'\''}ve been billed. In the meantime, feel free to explore our other
                            features and let us know if you have any questions.
                        </p>
                    </div>
                </>}
            />}
        </Card>

        {selectedRow && <InvoiceSendPopup
            isOpen={!!selectedRow}
            invoice={selectedRow}
            onClose={() => setSelectedRow(null)}
        />}
    </>
}
